import React, {ChangeEvent, Component, FormEvent} from "react";
import MaterialTextField from "@material-ui/core/TextField";
import {ValidatedFieldProps, ValidatedFieldState} from 'components/forms/validation';

export interface NumberFieldProps extends ValidatedFieldProps {
  disabled?: boolean;
  name?: string;
  label?: string;
  required?: boolean;
  onInput?: (ev:FormEvent<HTMLInputElement>)=>void;
  onChange?: (ev:ChangeEvent<HTMLInputElement>, value: number)=>void;
  className?: string;
  minValue?: number;
  maxValue?: number;
  increment?: number;
  value?: number;
}

interface NumberFieldState extends ValidatedFieldState {
  value: number|undefined;
}

export default class NumberField extends Component<NumberFieldProps, NumberFieldState> {
  constructor(props: NumberFieldProps) {
    super(props);
    this.state = {
      dirty: false,
      touched: false,
      validationMessage: undefined,
      value: props.value
    };
  }

  componentDidMount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.addFieldListener(this.props.name, this.onValidChanged);
    }
    if(this.props.value !== undefined) {
      this.setState({value: this.props.value});
    }
  }

  componentDidUpdate(prevProps: Readonly<NumberFieldProps>, prevState: Readonly<NumberFieldState>, snapshot?: any): void {
    if(this.props.value !== undefined && this.props.value !== prevProps.value) {
      this.setState({value: this.props.value});
    }
  }

  componentWillUnmount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.removeFieldListener(this.props.name, this.onValidChanged);
    }
  }

  onValidChanged = (errors: string[], forceDirty: boolean) => {
    const touched = this.state.touched || forceDirty;
    const dirty = this.state.dirty || forceDirty;
    if(errors) {
      let msg = this.props.validationMessages ? this.props.validationMessages[errors[0]] : null;
      if(!msg) {
        msg = errors[0];
      }
      this.setState({ validationMessage: msg, dirty: dirty, touched: touched });
    } else {
      this.setState({ validationMessage: undefined, dirty: dirty, touched: touched });
    }
  };

  onInput = (ev: FormEvent<HTMLInputElement>) => {
    const field = ev.target as HTMLInputElement;
    const value = field.value;
    // field.value will be empty if number is invalid, but will still display invalid value
    // set it to empty test in that case
    // TODO: allow partial invalid values (minus sign or period)
    field.value = value;
    const floatVal = value ? parseFloat(value) : undefined;
    this.setState({dirty: true}, () => {
      if(this.props.model && this.props.name) {
        this.props.model[this.props.name] = floatVal;
      }
      if(this.props.validationGroup && this.props.name) {
        this.props.validationGroup.validateField(this.props.name);
      }
    });
    if(this.props.onInput) {
      this.props.onInput(ev);
    }
  };

  onChange = (ev: ChangeEvent<HTMLInputElement>) => {
    if(this.props.onChange) {
      this.props.onChange(ev, parseFloat(ev.target.value) || 0);
    }
  };

  onBlur = () => {
    this.setState({touched: true});
  };

  render() {
    const inputProps: {[index:string]:any} = {};
    if(this.props.minValue !== undefined) {
      inputProps['min'] = this.props.minValue;
    }
    if(this.props.maxValue !== undefined) {
      inputProps['max'] = this.props.maxValue;
    }
    if(this.props.increment !== undefined) {
      inputProps['step'] = this.props.increment;
    }
    let value = this.state.value;
    if(this.props.model && this.props.name) {
      value = this.props.model[this.props.name];
    }
    const showError = this.state.touched && this.state.dirty && !!this.state.validationMessage;
    const fullClassName = this.props.className ? `number-field ${this.props.className}` : 'number-field';
    return <MaterialTextField variant="outlined" onInput={this.onInput} disabled={this.props.disabled}
                              required={this.props.required} label={this.props.label} type='number'
                              onBlur={this.onBlur} error={showError} className={fullClassName}
                              helperText={showError ? this.state.validationMessage : undefined}
                              inputProps={inputProps} value={value} onChange={this.onChange} />
  }
}